// Schema-validated JSON output parsing with up-to-2 retries (PRD §9.6).
// Returns either a parsed-and-validated result or a degraded-confidence flag —
// never a silent failure.
//
// Validator note: uses `./json-schema-check` (a CSP-safe subset validator)
// rather than Ajv. Ajv compiles validators via `new Function(...)`, which the
// MV3 extension CSP (`script-src 'self' 'wasm-unsafe-eval'`) refuses. The
// subset covers exactly the features used by `prompts/*` schemas.

import { compile, type ValidateFn } from './json-schema-check';
import type { ChatRequest, LlmClient } from './types';

const compiledCache = new WeakMap<object, ValidateFn>();

function getValidator(schema: object): ValidateFn {
  let v = compiledCache.get(schema);
  if (!v) {
    v = compile(schema);
    compiledCache.set(schema, v);
  }
  return v;
}

export interface ValidatedCall<T> {
  ok: boolean;
  data?: T;
  /** Last raw response text, for debugging or audit-log payload. */
  rawText?: string;
  /** Set when ok=false. */
  errors?: string[];
  attempts: number;
  promptVersion: string;
  tokens?: number;
}

const MAX_ATTEMPTS = 3; // 1 initial + 2 retries

export async function callWithSchema<T>(
  client: LlmClient,
  request: ChatRequest,
  parse: (json: unknown) => T | null,
): Promise<ValidatedCall<T>> {
  if (!request.schema) {
    throw new Error('callWithSchema requires a schema on the request');
  }
  const validator = getValidator(request.schema);

  let lastErrors: string[] = [];
  let lastText: string | undefined;
  let lastTokens: number | undefined;

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    let reply;
    try {
      reply = await client.chat(request);
    } catch (err) {
      // The WebLLM engine itself threw (e.g. unsupported response_format,
      // context overflow, worker crash). Without this the rejection
      // propagated as an unhandled error and the UI only showed a generic
      // "Draft failed" / a stuck "enriching…". Treat it as a retryable
      // attempt and, if it persists, return it as a visible structured error.
      lastErrors = [`engine error: ${err instanceof Error ? err.message : String(err)}`];
      continue;
    }
    lastText = reply.text;
    lastTokens = reply.tokens;
    if (reply.json === undefined) {
      lastErrors = ['response was not parseable JSON'];
      continue;
    }
    if (!validator(reply.json)) {
      lastErrors = (validator.errors ?? []).map((e) => `${e.instancePath || '<root>'} ${e.message ?? ''}`);
      continue;
    }
    const data = parse(reply.json);
    if (data === null) {
      lastErrors = ['response matched schema but parse() rejected (value-level constraint)'];
      continue;
    }
    return {
      ok: true,
      data,
      rawText: lastText,
      attempts: attempt,
      promptVersion: request.promptVersion,
      tokens: lastTokens,
    };
  }
  return {
    ok: false,
    rawText: lastText,
    errors: lastErrors,
    attempts: MAX_ATTEMPTS,
    promptVersion: request.promptVersion,
    tokens: lastTokens,
  };
}
